<template>
  <form @submit.prevent="submitForm" class="cp-form">
    <div class="cp-card">
      <div class="card-head">
        <div class="head-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 018 0v4" stroke-linecap="round" />
          </svg>
        </div>
        <div>
          <div class="head-title">Password</div>
          <div class="head-sub">Enter your current password, then choose a new one.</div>
        </div>
      </div>

      <div class="field-group">
        <div class="field" :class="{ 'field-invalid': !currentPassword.isValid }">
          <label for="current-password">Current password</label>
          <div class="input-wrap">
            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 018 0v4" stroke-linecap="round" />
            </svg>
            <input
              :type="show.current ? 'text' : 'password'"
              id="current-password"
              v-model.trim="currentPassword.val"
              @focus="clearValidity('currentPassword')"
              placeholder="Your current password"
              autocomplete="current-password"
            />
            <button type="button" class="eye-btn" @click="show.current = !show.current" :title="show.current ? 'Hide' : 'Show'">
              <svg v-if="!show.current" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" stroke-linecap="round" stroke-linejoin="round" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a19.77 19.77 0 015.06-6.06M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a19.5 19.5 0 01-3.17 4.19M14.12 14.12A3 3 0 019.88 9.88" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M1 1l22 22" stroke-linecap="round" />
              </svg>
            </button>
          </div>
          <p v-if="!currentPassword.isValid" class="field-error">{{ currentPassword.msg }}</p>
        </div>

        <div class="field" :class="{ 'field-invalid': !newPassword.isValid }">
          <label for="new-password">New password</label>
          <div class="input-wrap">
            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M12 15v2M6 21h12a2 2 0 002-2v-8a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2z" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M8 11V7a4 4 0 018 0v4" stroke-linecap="round" />
            </svg>
            <input
              :type="show.next ? 'text' : 'password'"
              id="new-password"
              v-model.trim="newPassword.val"
              @focus="clearValidity('newPassword')"
              placeholder="At least 6 characters"
              autocomplete="new-password"
            />
            <button type="button" class="eye-btn" @click="show.next = !show.next" :title="show.next ? 'Hide' : 'Show'">
              <svg v-if="!show.next" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" stroke-linecap="round" stroke-linejoin="round" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a19.77 19.77 0 015.06-6.06M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a19.5 19.5 0 01-3.17 4.19M14.12 14.12A3 3 0 019.88 9.88" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M1 1l22 22" stroke-linecap="round" />
              </svg>
            </button>
          </div>

          <!-- Strength meter -->
          <div v-if="newPassword.val" class="strength">
            <div class="strength-bar">
              <span
                v-for="i in 4"
                :key="i"
                class="seg"
                :class="i <= strength.score ? `seg-${strength.tone}` : ''"
              ></span>
            </div>
            <span class="strength-label" :class="`label-${strength.tone}`">{{ strength.label }}</span>
          </div>

          <p v-if="!newPassword.isValid" class="field-error">{{ newPassword.msg }}</p>
        </div>

        <div class="field" :class="{ 'field-invalid': !confirmPassword.isValid }">
          <label for="confirm-password">Confirm new password</label>
          <div class="input-wrap">
            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M6 21h12a2 2 0 002-2v-8a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2z" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M8 11V7a4 4 0 018 0v4" stroke-linecap="round" />
            </svg>
            <input
              :type="show.confirm ? 'text' : 'password'"
              id="confirm-password"
              v-model.trim="confirmPassword.val"
              @focus="clearValidity('confirmPassword')"
              placeholder="Repeat your new password"
              autocomplete="new-password"
            />
            <button type="button" class="eye-btn" @click="show.confirm = !show.confirm" :title="show.confirm ? 'Hide' : 'Show'">
              <svg v-if="!show.confirm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" stroke-linecap="round" stroke-linejoin="round" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a19.77 19.77 0 015.06-6.06M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a19.5 19.5 0 01-3.17 4.19M14.12 14.12A3 3 0 019.88 9.88" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M1 1l22 22" stroke-linecap="round" />
              </svg>
            </button>
            <span v-if="confirmPassword.val && confirmPassword.val === newPassword.val" class="match-tick" title="Passwords match">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
          </div>
          <p v-if="!confirmPassword.isValid" class="field-error">{{ confirmPassword.msg }}</p>
        </div>
      </div>

      <div class="tips">
        <div class="tips-title">Password tips</div>
        <ul>
          <li :class="{ 'tip-met': newPassword.val.length >= 6 }">
            <span class="tip-check">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
                <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
            At least 6 characters
          </li>
          <li :class="{ 'tip-met': /[A-Z]/.test(newPassword.val) && /[a-z]/.test(newPassword.val) }">
            <span class="tip-check">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
                <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
            Mix of upper and lower case
          </li>
          <li :class="{ 'tip-met': /\d/.test(newPassword.val) && /[^A-Za-z0-9]/.test(newPassword.val) }">
            <span class="tip-check">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
                <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
            Include a number and a symbol
          </li>
        </ul>
      </div>

      <p v-if="!formIsValid" class="form-alert">Please fix the errors above and try again.</p>

      <div class="card-actions">
        <button type="submit" class="btn btn-primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
            <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Change Password
        </button>
      </div>
    </div>
  </form>
</template>

<script>
export default {
  name: "change-password-form",
  emits: ["change-password"],
  data() {
    return {
      currentPassword: { val: "", isValid: true, msg: "" },
      newPassword: { val: "", isValid: true, msg: "" },
      confirmPassword: { val: "", isValid: true, msg: "" },
      formIsValid: true,
      show: { current: false, next: false, confirm: false },
    };
  },
  computed: {
    strength() {
      const v = this.newPassword.val || "";
      let score = 0;
      if (v.length >= 6) score++;
      if (v.length >= 10) score++;
      if (/[A-Z]/.test(v) && /[a-z]/.test(v)) score++;
      if (/\d/.test(v) && /[^A-Za-z0-9]/.test(v)) score++;
      const tones = ["weak", "weak", "fair", "good", "strong"];
      const labels = ["Too short", "Weak", "Fair", "Good", "Strong"];
      return { score, tone: tones[score], label: labels[score] };
    },
  },
  methods: {
    clearValidity(input) {
      this[input].isValid = true;
    },
    validateForm() {
      this.formIsValid = true;

      if (!this.currentPassword.val) {
        this.currentPassword.isValid = false;
        this.currentPassword.msg = "Current password is required.";
        this.formIsValid = false;
      }

      if (!this.newPassword.val || this.newPassword.val.length < 6) {
        this.newPassword.isValid = false;
        this.newPassword.msg = "New password must be at least 6 characters.";
        this.formIsValid = false;
      }

      if (this.newPassword.val !== this.confirmPassword.val) {
        this.confirmPassword.isValid = false;
        this.confirmPassword.msg = "Passwords do not match.";
        this.formIsValid = false;
      }
    },
    submitForm() {
      this.validateForm();
      if (!this.formIsValid) return;

      this.$emit("change-password", {
        currentPassword: this.currentPassword.val,
        newPassword: this.newPassword.val,
      });

      this.currentPassword.val = "";
      this.newPassword.val = "";
      this.confirmPassword.val = "";
    },
  },
};
</script>

<style scoped>
.cp-form {
  display: flex;
  flex-direction: column;
}

.cp-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 22px;
  max-width: 560px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
  box-shadow: 0 10px 30px -18px rgba(15, 23, 42, 0.18);
}

.card-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f1f5f9;
}
.head-icon {
  width: 42px; height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #eef2f7 0%, #eef2f7 100%);
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.head-icon svg { width: 22px; height: 22px; }
.head-title { font-size: 15px; font-weight: 700; color: #0f172a; }
.head-sub { font-size: 12px; color: #64748b; margin-top: 2px; }

.field-group {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
  letter-spacing: 0.01em;
}
.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.input-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.input-wrap input {
  width: 100%;
  padding: 10px 68px 10px 38px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.input-wrap input::placeholder { color: #cbd5e1; }
.input-wrap input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.10);
}
.field-invalid .input-wrap input {
  border-color: #fca5a5;
  background: #fef2f2;
}
.field-invalid .input-wrap input:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.10);
}
.eye-btn {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 28px; height: 28px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
}
.eye-btn:hover { background: #f1f5f9; color: #475569; }
.eye-btn svg { width: 16px; height: 16px; }

.match-tick {
  position: absolute;
  right: 40px;
  color: #059669;
  display: inline-flex;
}
.match-tick svg { width: 16px; height: 16px; }

.field-error {
  font-size: 12px;
  color: #dc2626;
  margin-top: 6px;
  font-weight: 500;
}

/* Strength meter */
.strength {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}
.strength-bar {
  display: flex;
  gap: 4px;
  flex: 1;
}
.seg {
  flex: 1;
  height: 4px;
  border-radius: 999px;
  background: #e2e8f0;
  transition: background 0.2s;
}
.seg-weak    { background: #ef4444; }
.seg-fair    { background: #f59e0b; }
.seg-good    { background: #3b82f6; }
.seg-strong  { background: #059669; }
.strength-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  min-width: 68px;
  text-align: right;
}
.label-weak    { color: #dc2626; }
.label-fair    { color: #b45309; }
.label-good    { color: #1d4ed8; }
.label-strong  { color: #047857; }

/* Tips */
.tips {
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 12px 14px;
}
.tips-title {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 700;
  margin-bottom: 8px;
}
.tips ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tips li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: #64748b;
  transition: color 0.15s;
}
.tip-check {
  width: 18px; height: 18px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  flex-shrink: 0;
}
.tip-check svg { width: 11px; height: 11px; }
.tip-met { color: #047857; font-weight: 500; }
.tip-met .tip-check { background: #10b981; }

.form-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  margin: 0;
  font-weight: 500;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
}
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-icon { width: 15px; height: 15px; }
</style>
